import React, { useMemo, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { usePaymentDesignTokens, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING, PAYMENT_BORDER_RADIUS, PAYMENT_AVATAR_SIZE } from '@/constants/residents/mobile/paymentDesignTokens';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { getIsOverdue, getInCredit } from '@/features/payments/common/helpers';
import { PaymentInfoBubbleModal, BalanceState } from './PaymentInfoBubbleModal';
import { RecurringChargesModal, RecurringCharge } from './RecurringChargesModal';
import { ChargeBreakdown } from '../shared/ChargeBreakdown';
import { AccountCreditIndicator } from '../shared/AccountCreditIndicator';
import type { BalanceItem } from '@/types/residents/lease';
import { PrimaryLeaseholderTooltip } from './PrimaryLeaseholderTooltip';
import { PendingPaymentsModal } from './PendingPaymentsModal';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { getInitials } from '@/utils/residents/utils';
import { strings } from '@/constants/residents';

const InfoIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;
const ChevronRight = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const HouseIcon = RESIDENTS_SVG_COMPONENTS.HOUSE;
const RefreshIcon = RESIDENTS_SVG_COMPONENTS.REFRESH;
const PendingCheckIcon = RESIDENTS_SVG_COMPONENTS.PENDING_CHECK;

export interface MobilePaymentCardV2Props {
  /** Lease ID - used for navigation and modal */
  leaseId: number;
  /** Display name for the resident/account */
  residentName: string;
  /** Property marketing name */
  propertyName: string;
  /** Unit number/identifier */
  unitNumber: string;
  /** Current balance due amount */
  balanceDue: number;
  /** Unapplied prepay / account credit amount (positive number; 0 when none) */
  accountCredit?: number;
  /** Itemized charge breakdown from the balance endpoint */
  balanceItems?: BalanceItem[];
  /** Whether autopay is enabled for this lease */
  autopayEnabled?: boolean;
  /** Whether this is a connected account (not the current user's lease) */
  isConnection?: boolean;
  /** Whether payments are blocked for this lease */
  paymentsBlocked?: boolean;
  /** Whether the current user is the primary leaseholder */
  isPrimaryLeaseholder?: boolean;
  /** Recurring/scheduled charges to display */
  scheduledCharges?: RecurringCharge[];
  /** Whether to show "See details" link (for multi-lease view) */
  showSeeDetails?: boolean;
  /** User's profile photo URL */
  photoUrl?: string;
  /** User's first name (for avatar initials) */
  firstName?: string;
  /** User's last name (for avatar initials) */
  lastName?: string;
  /** Callback when autopay is updated/removed */
  onAutopayUpdated?: () => void;
  /** Number of pending payments (shows "Payments pending" indicator if > 0) */
  pendingPaymentsCount?: number;
}

/**
 * Mobile Payment Card V2
 * Matches Figma design for payment cards with all states
 * States: balance owed, zero balance, credit, overdue, no payments accepted
 */
export const MobilePaymentCardV2: React.FC<MobilePaymentCardV2Props> = ({
  leaseId,
  residentName,
  propertyName,
  unitNumber,
  balanceDue,
  accountCredit = 0,
  balanceItems,
  autopayEnabled = false,
  isConnection = false,
  paymentsBlocked = false,
  isPrimaryLeaseholder = false,
  scheduledCharges = [],
  showSeeDetails = false,
  photoUrl,
  firstName,
  lastName,
  onAutopayUpdated,
  pendingPaymentsCount = 0,
}) => {
  const pt = usePaymentDesignTokens();

  const styles = useMemo(() => StyleSheet.create({
    cardWrapper: {
      marginBottom: PAYMENT_SPACING.lg,
    },
    gradientBorder: {
      borderRadius: PAYMENT_BORDER_RADIUS.xl,
      padding: 2,
      // Gradient border simulation - can be enhanced with LinearGradient if needed
      backgroundColor: pt.PINK_ACCENT,
      overflow: 'hidden',
    },
    noBorder: {
      // No border wrapper for zero/credit balance states
    },
    card: {
      backgroundColor: pt.CARD_BACKGROUND,
      borderRadius: PAYMENT_BORDER_RADIUS.xl - 2,
      overflow: 'hidden',
    },
    cardNoBorder: {
      // When no border, use full border radius
      borderRadius: PAYMENT_BORDER_RADIUS.xl,
    },
    overdueBanner: {
      backgroundColor: pt.ERROR_RED,
      paddingVertical: PAYMENT_SPACING.sm,
      paddingHorizontal: PAYMENT_SPACING.lg,
      alignItems: 'flex-start',
    },
    overdueBannerText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium,
      color: pt.TEXT_PRIMARY,
    },
    cardContent: {
      padding: PAYMENT_SPACING.xl,
    },
    topSection: {
      flexDirection: 'row',
      gap: 18, // Figma: gap-[18px] between avatar and info
    },
    avatarContainer: {
      width: PAYMENT_AVATAR_SIZE.lg,
      height: PAYMENT_AVATAR_SIZE.lg,
      borderRadius: PAYMENT_AVATAR_SIZE.lg / 2,
      backgroundColor: pt.AVATAR_BACKGROUND,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    avatarImage: {
      width: '100%',
      height: '100%',
    },
    avatarInitials: {
      ...PAYMENT_TYPOGRAPHY.heading2xl,
      color: pt.AVATAR_TEXT,
      fontSize: 20,
    },
    infoSection: {
      flex: 1,
      gap: PAYMENT_SPACING.xs,
    },
    badgeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6, // Figma: gap-[6px]
    },
    primaryIconContainer: {
      width: 22,
      height: 22,
      borderRadius: 20, // Figma: rounded-[20px] - circular
      backgroundColor: 'rgba(238, 241, 244, 0.1)', // Figma: bg-[rgba(238,241,244,0.1)]
      justifyContent: 'center',
      alignItems: 'center',
    },
    currentLeaseBadge: {
      backgroundColor: 'rgba(238, 241, 244, 0.1)', // Figma: bg-[rgba(238,241,244,0.1)]
      paddingHorizontal: 10, // Figma: px-[10px]
      height: 24, // Figma: h-[24px]
      justifyContent: 'center',
      borderRadius: 60, // Figma: rounded-[60px] - pill shape
      alignSelf: 'flex-start',
    },
    currentLeaseBadgeText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium,
      color: pt.TEXT_PRIMARY, // Figma: text-[#eef1f4]
    },
    residentName: {
      ...PAYMENT_TYPOGRAPHY.headingLg,
      color: pt.TEXT_PRIMARY,
      marginTop: PAYMENT_SPACING.xs,
    },
    propertyText: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_PRIMARY,
    },
    statusText: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_MUTED,
      marginTop: PAYMENT_SPACING.xs,
    },
    linkButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: PAYMENT_SPACING.xs,
      marginTop: PAYMENT_SPACING.sm,
    },
    linkButtonText: {
      ...PAYMENT_TYPOGRAPHY.bodySmMedium,
      color: pt.BLUE_LINK,
    },
    // Bottom section wrapper - aligned with info text (avatar width + gap = 68 + 18 = 86)
    bottomSection: {
      marginLeft: PAYMENT_AVATAR_SIZE.lg + 18, // 68 + 18 = 86px - matches Figma
      paddingTop: PAYMENT_SPACING.lg,
    },
    separatorLine: {
      height: 1,
      backgroundColor: pt.BORDER, // #464646
      marginBottom: PAYMENT_SPACING.lg, // 16px gap before balance
    },
    balanceSection: {
      marginBottom: 18, // Figma: gap-[18px] before autopay/button group
    },
    balanceRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between', // Push info icon to right edge
      width: '100%',
    },
    balanceAmountContainer: {
      flex: 1, // Take remaining space, push info icon to right
    },
    balanceAmount: {
      fontSize: 24,
      fontWeight: '700',
      color: pt.TEXT_PRIMARY,
      lineHeight: 36,
    },
    balanceAmountOverdue: {
      color: pt.ERROR_RED,
    },
    balanceAmountCredit: {
      color: pt.SUCCESS_GREEN,
    },
    infoButton: {
      // No extra padding - icon is 20x20, hitSlop handles tap area
    },
    balanceLabel: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_MUTED,
    },
    autopayLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: PAYMENT_SPACING.xs,
      marginBottom: PAYMENT_SPACING.md, // 12px gap before button - Figma: gap-[12px]
    },
    autopayActiveText: {
      ...PAYMENT_TYPOGRAPHY.bodySmMedium,
      color: pt.SUCCESS_GREEN,
    },
    autopaySetupText: {
      ...PAYMENT_TYPOGRAPHY.bodySmMedium,
      color: pt.PINK_ACCENT,
    },
    makePaymentButton: {
      backgroundColor: pt.BUTTON_PRIMARY,
      borderRadius: PAYMENT_BORDER_RADIUS.sm, // 8px from Figma
      paddingVertical: PAYMENT_SPACING.sm, // 8px
      paddingHorizontal: PAYMENT_SPACING.lg, // 16px
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'flex-start', // Don't stretch full width
      height: 34, // Fixed height from Figma
    },
    makePaymentButtonOutline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: pt.BUTTON_PRIMARY,
    },
    makePaymentButtonText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium, // 12px font from Figma
      color: pt.BUTTON_PRIMARY_TEXT, // White text
    },
    makePaymentButtonTextOutline: {
      color: pt.BUTTON_PRIMARY, // Pink text for outline
    },
    pendingPaymentsLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: PAYMENT_SPACING.xs,
      marginTop: PAYMENT_SPACING.xl, // Figma: pt-[20px] with border-top
      paddingTop: PAYMENT_SPACING.xl,
      borderTopWidth: 1,
      borderTopColor: pt.BORDER, // #464646
    },
    pendingPaymentsText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium,
      color: pt.TEXT_MUTED,
    },
    chargesSpacing: {
      marginBottom: PAYMENT_SPACING.lg,
    },
}), [pt]);

  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  
  const [showInfoBubble, setShowInfoBubble] = useState(false);
  const [showRecurringCharges, setShowRecurringCharges] = useState(false);
  const [showPrimaryTooltip, setShowPrimaryTooltip] = useState(false);
  const [showAutopayModal, setShowAutopayModal] = useState(false);
  const [autopayModalMode, setAutopayModalMode] = useState<'view' | 'setup'>('view');
  const [showPendingModal, setShowPendingModal] = useState(false);

  // Determine balance state
  const isOverdue = useMemo(() => getIsOverdue(balanceDue), [balanceDue]);
  const inCredit = useMemo(() => getInCredit(balanceDue), [balanceDue]);
  const hasBalance = balanceDue > 0;
  const isZeroBalance = balanceDue === 0;

  // Get balance state for info bubble
  const balanceState: BalanceState = useMemo(() => {
    if (isOverdue) return 'past_due';
    if (inCredit) return 'credit';
    if (isZeroBalance) return 'zero';
    return 'owed';
  }, [isOverdue, inCredit, isZeroBalance]);

  // Get status message based on state
  const getStatusMessage = (): string => {
    if (paymentsBlocked) {
      return 'No payments are being accepted at this time. Please contact your community manager if you have questions.';
    }
    if (isOverdue) {
      return 'This account has overdue charges. Payments are due by the 1st of each month.';
    }
    if (isZeroBalance || inCredit) {
      return 'Great! This account has no outstanding charges.';
    }
    return 'This account has outstanding charges. Payments are due by the 1st of each month.';
  };

  // Format balance display
  const formatBalanceDisplay = (): string => {
    if (inCredit) {
      return `(${formatCurrency(Math.abs(balanceDue))})`;
    }
    return formatCurrency(balanceDue);
  };

  // Get balance label
  const getBalanceLabel = (): string => {
    if (inCredit) return 'This account has a credit';
    if (isZeroBalance) return 'No balance due';
    return 'Balance due';
  };

  // Get avatar initials
  const initials = getInitials(firstName, lastName) || residentName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const handleMakePayment = () => {
    navigation.navigate('MakePayment', { leaseId });
  };

  const handleSeeDetails = () => {
    navigation.navigate('PaymentDetail', { leaseId });
  };

  const handleAutopayPress = () => {
    if (autopayEnabled) {
      // Show autopay details modal (modal fetches its own data)
      setAutopayModalMode('view');
      setShowAutopayModal(true);
    } else {
      // Show autopay setup modal
      setAutopayModalMode('setup');
      setShowAutopayModal(true);
    }
  };

  const handleAutopayModalClose = () => {
    setShowAutopayModal(false);
  };

  const handleAutopayUpdated = () => {
    onAutopayUpdated?.();
  };

  // Determine if we should show the border (only for balance owed or overdue)
  const showBorder = hasBalance && !inCredit;

  return (
    <View style={styles.cardWrapper}>
      {/* Conditional Border Wrapper - only show for balance due states */}
      <View style={showBorder ? styles.gradientBorder : styles.noBorder}>
        <View style={[styles.card, !showBorder && styles.cardNoBorder]}>
          {/* Overdue Banner */}
          {isOverdue && (
            <View style={styles.overdueBanner}>
              <Text style={styles.overdueBannerText}>Past due balance!</Text>
            </View>
          )}

          {/* Card Content */}
          <View style={styles.cardContent}>
            {/* Top Section - Avatar and Info */}
            <View style={styles.topSection}>
              {/* Avatar */}
              <View style={styles.avatarContainer}>
                {photoUrl ? (
                  <Image
                    source={{ uri: photoUrl }}
                    style={styles.avatarImage}
                    resizeMode="cover"
                  />
                ) : (
                  <Text style={styles.avatarInitials}>{initials}</Text>
                )}
              </View>

              {/* Info Section */}
              <View style={styles.infoSection}>
                {/* Current Lease Badge with optional Primary icon */}
                <View style={styles.badgeRow}>
                  {isPrimaryLeaseholder && HouseIcon && (
                    <TouchableOpacity
                      style={styles.primaryIconContainer}
                      onPress={() => setShowPrimaryTooltip(true)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <HouseIcon width={12} height={12} color={pt.TEXT_MUTED} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.currentLeaseBadge}>
                    <Text style={styles.currentLeaseBadgeText}>{strings.mobilePayments.currentLease}</Text>
                  </View>
                </View>

                {/* Name */}
                <Text style={styles.residentName} numberOfLines={1}>
                  {isConnection ? residentName : 'My account'}
                </Text>

                {/* Property Info */}
                <Text style={styles.propertyText} numberOfLines={1}>
                  {propertyName}
                  {unitNumber ? `, Unit ${unitNumber}` : ''}
                </Text>

                {/* Status Message */}
                <Text style={styles.statusText} numberOfLines={3}>
                  {getStatusMessage()}
                </Text>

                {/* Show recurring charges link (only when zero/credit balance) */}
                {(isZeroBalance || inCredit) && !paymentsBlocked && scheduledCharges.length > 0 && (
                  <TouchableOpacity
                    style={styles.linkButton}
                    onPress={() => setShowRecurringCharges(true)}
                  >
                    <Text style={styles.linkButtonText}>Show recurring charges</Text>
                  </TouchableOpacity>
                )}

                {/* See Details link (for multiple leases view) */}
                {showSeeDetails && (
                  <TouchableOpacity
                    style={styles.linkButton}
                    onPress={handleSeeDetails}
                  >
                    <Text style={styles.linkButtonText}>See details</Text>
                    {ChevronRight && (
                      <ChevronRight width={16} height={16} color={pt.BLUE_LINK} />
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* Bottom Section - Balance, Autopay, Button - aligned with info text */}
            {!paymentsBlocked && (
              <View style={styles.bottomSection}>
                {/* Separator Line */}
                <View style={styles.separatorLine} />

                {/* Itemized Charges */}
                <ChargeBreakdown
                  balanceItems={balanceItems || []}
                  balanceDue={balanceDue}
                  accountCredit={accountCredit}
                  variant="mobile"
                  style={styles.chargesSpacing}
                />

                {/* Account Credit indicator — renders only when accountCredit > 0 */}
                <AccountCreditIndicator amount={accountCredit} variant="inline" />

                {/* Balance Section */}
                <View style={styles.balanceSection}>
                  {/* Balance Row - amount on left, info icon on right */}
                  <View style={styles.balanceRow}>
                    <View style={styles.balanceAmountContainer}>
                      <Text style={[
                        styles.balanceAmount,
                        isOverdue && styles.balanceAmountOverdue,
                        inCredit && styles.balanceAmountCredit,
                      ]}>
                        {formatBalanceDisplay()}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={styles.infoButton}
                      onPress={() => setShowInfoBubble(true)}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      {InfoIcon && (
                        <InfoIcon width={20} height={20} color={pt.TEXT_MUTED} />
                      )}
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.balanceLabel}>{getBalanceLabel()}</Text>
                </View>

                {/* Autopay Link */}
                <TouchableOpacity
                  style={styles.autopayLink}
                  onPress={handleAutopayPress}
                >
                  {autopayEnabled ? (
                    // Autopay Active - Green text with chevron
                    <>
                      <Text style={styles.autopayActiveText}>Autopay active</Text>
                      {ChevronRight && (
                        <ChevronRight width={16} height={16} color={pt.SUCCESS_GREEN} />
                      )}
                    </>
                  ) : (
                    // Autopay Setup - Pink text with refresh icon
                    <>
                      {RefreshIcon && (
                        <RefreshIcon width={16} height={16} color={pt.PINK_ACCENT} />
                      )}
                      <Text style={styles.autopaySetupText}>Autopay setup</Text>
                    </>
                  )}
                </TouchableOpacity>

                {/* Make Payment Button */}
                <TouchableOpacity
                  style={[
                    styles.makePaymentButton,
                    (isZeroBalance || inCredit) && styles.makePaymentButtonOutline,
                  ]}
                  onPress={handleMakePayment}
                >
                  <Text style={[
                    styles.makePaymentButtonText,
                    (isZeroBalance || inCredit) && styles.makePaymentButtonTextOutline,
                  ]}>
                    Make a payment
                  </Text>
                </TouchableOpacity>

                {/* Pending Payments Link */}
                {pendingPaymentsCount > 0 && (
                  <TouchableOpacity
                    style={styles.pendingPaymentsLink}
                    onPress={() => setShowPendingModal(true)}
                  >
                    {PendingCheckIcon && (
                      <PendingCheckIcon width={20} height={20} />
                    )}
                    <Text style={styles.pendingPaymentsText}>
                      {strings.mobilePayments.paymentsPending}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Info Bubble Modal */}
      <PaymentInfoBubbleModal
        visible={showInfoBubble}
        onClose={() => setShowInfoBubble(false)}
        balanceState={balanceState}
      />

      {/* Recurring Charges Modal */}
      <RecurringChargesModal
        visible={showRecurringCharges}
        onClose={() => setShowRecurringCharges(false)}
        charges={scheduledCharges}
      />

      {/* Primary Leaseholder Tooltip */}
      <PrimaryLeaseholderTooltip
        visible={showPrimaryTooltip}
        onClose={() => setShowPrimaryTooltip(false)}
      />

      {/* Autopay Modal - fetches its own data using leaseId */}
      <MobileAutopayDetailsModal
        visible={showAutopayModal}
        onClose={handleAutopayModalClose}
        leaseId={leaseId}
        onAutopayUpdated={handleAutopayUpdated}
        mode={autopayModalMode}
      />

      {/* Pending Payments Modal */}
      <PendingPaymentsModal
        visible={showPendingModal}
        onClose={() => setShowPendingModal(false)}
      />
    </View>
  );
};

